import type { PanelPosition } from "@allergy123/contracts/clinical";
import { useState } from "react";
import { GROUP_LABELS, groupOf, parseReading, trayBlocks } from "../lib/tray";

export type Readings = Record<string, number | null>;

/** The tray as the technician sees it: twelve blocks of five wells; empty means not read, 0 means no wheal (Q-09). */
export function ReactionsGrid({ positions, readings, onChange, disabled = false }: { positions: PanelPosition[]; readings: Readings; onChange: (antigenId: string, value: number | null) => void; disabled?: boolean }) {
  const [invalid, setInvalid] = useState<Record<string, boolean>>({});
  const blocks = trayBlocks(positions);
  const read = positions.filter((p) => readings[p.antigenId] !== null && readings[p.antigenId] !== undefined).length;
  return (
    <div className="flex flex-col gap-4">
      <p className="text-small text-ink-secondary">
        {read} of {positions.length} wells read. Whole millimetres, 0 to 21; leave a well empty if it was not read.
      </p>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {blocks.map((block, i) => {
          const first = block[0];
          const group = first ? groupOf(first.position) : i + 1;
          return (
            <fieldset key={i} className="rounded-md border border-rule-strong bg-card p-3" disabled={disabled}>
              <legend className="px-1 text-small font-medium text-ink-secondary">
                Block {i + 1} · {GROUP_LABELS[group]}
              </legend>
              <ol className="flex flex-col gap-2">
                {block.map((p) => {
                  const id = `well-${p.position}`;
                  const v = readings[p.antigenId];
                  return (
                    <li key={p.antigenId} className="grid grid-cols-[2rem_1fr_4.5rem] items-center gap-2">
                      <span className="text-small text-ink-secondary">{p.position}</span>
                      <label htmlFor={id} className="truncate text-body" title={p.displayName}>
                        {p.abbreviation || p.displayName}
                        {p.controlKind && <span className="ml-1 text-small text-ink-secondary">({p.controlKind === "positive" ? "histamine" : "saline"})</span>}
                      </label>
                      <input
                        id={id}
                        inputMode="numeric"
                        pattern="[0-9]*"
                        aria-label={`${p.displayName}, position ${p.position}, millimetres`}
                        aria-invalid={invalid[p.antigenId] || undefined}
                        className={`min-h-9 rounded-sm bg-card px-2 text-right text-body tabular-nums text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus ${invalid[p.antigenId] ? "border-2 border-rule-strong" : "border border-rule-strong"}`}
                        defaultValue={v === null || v === undefined ? "" : String(v)}
                        onChange={(e) => {
                          const parsed = parseReading(e.target.value);
                          setInvalid((s) => ({ ...s, [p.antigenId]: parsed === "invalid" }));
                          if (parsed !== "invalid") onChange(p.antigenId, parsed);
                        }}
                      />
                    </li>
                  );
                })}
              </ol>
            </fieldset>
          );
        })}
      </div>
    </div>
  );
}
